// api/parseChatResponse.js
import { decodeBase64ToBytes } from '../utils/base64Utils';

/**
 * Reads and parses the streaming JSON lines from the server response.
 *
 * @param {Response} response - The fetch response object
 * @param {(text: string) => void} onTextChunk - callback for partial text
 * @param {(pcm: Uint8Array) => void} onAudioChunk - callback for PCM audio
 * @param {(sources: Array) => void} [onSourcesChunk] - callback for an array of sources
 * @param {(actions: Array) => void} [onActionsChunk] - callback for an array of actions
 * @returns {Promise<void>}
 */
export async function parseChatResponse(response, onTextChunk, onAudioChunk, onSourcesChunk, onActionsChunk) {
  if (!response.ok) {
    throw new Error(`chataudio request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  let leftoverText = '';
  let leftoverPCM = new Uint8Array(0);

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    const chunkText = new TextDecoder().decode(value, { stream: true });
    leftoverText += chunkText;

    // Split lines on newline
    const lines = leftoverText.split('\n');
    leftoverText = lines.pop() || ''; // keep final partial line in leftover

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) continue;

      let parsed;
      try {
        parsed = JSON.parse(line);
      } catch (err) {
        console.error('Could not parse JSON line:', line, err);
        continue;
      }

      switch (parsed.type) {
        case 'text': {
          onTextChunk?.(parsed.data);
          break;
        }
        case 'audio': {
          const chunkBytes = decodeBase64ToBytes(parsed.data);
          const combined = concatPCM(leftoverPCM, chunkBytes);
          const aligned = alignTo16Bit(combined);

          if (aligned.length > 0) {
            onAudioChunk?.(aligned);
          }
          leftoverPCM = handlePCMLeftover(combined);
          break;
        }
        case 'sources': {
          // new chunk type for article references
          onSourcesChunk?.(parsed.data);
          break;
        }
        case 'actions': {
          onActionsChunk?.(parsed.data);
          break;
        }
        case 'end': {
          return; // streaming complete
        }
        default: {
          console.warn('Unknown chunk type:', parsed.type);
        }
      }
    }
  }
}

/** Concatenate leftover + new bytes */
function concatPCM(leftover, current) {
  const combined = new Uint8Array(leftover.length + current.length);
  combined.set(leftover, 0);
  combined.set(current, leftover.length);
  return combined;
}

/** Align combined PCM data to 16-bit boundaries */
function alignTo16Bit(data) {
  const alignedLen = data.length - (data.length % 2);
  return data.slice(0, alignedLen);
}

/** Keep leftover bytes if any are unaligned */
function handlePCMLeftover(combined) {
  const remainder = combined.length % 2;
  if (remainder > 0) {
    return combined.slice(combined.length - remainder);
  }
  return new Uint8Array(0);
}
